import { useState } from 'react'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { BatchRowRecord } from './shared'
import styles from './runsTable.module.scss'

const BATCH_FILE_URL = 'viewthru-batch-file'

type FileKind = 'credit' | 'aml' | 'json'

/**
 * `compact` packs the tags into two columns for a dense table cell; `row` lays
 * them out in a single line where there is space to breathe.
 */
type TagLayout = 'compact' | 'row'

const tagContainerClass = (layout: TagLayout) =>
    `${styles.fileTags} ${layout === 'row' ? styles.fileTagsRow : ''}`

interface TagProps {
    row: BatchRowRecord
    layout?: TagLayout
}

/** Each tag downloads its artefact through a short-lived presigned URL. */
export const FileTags = ({ row, layout = 'compact' }: TagProps) => {
    const [busyKind, setBusyKind] = useState<FileKind | null>(null)

    const files: { label: string; kind: FileKind; key: string | null }[] = [
        { label: 'PDF', kind: 'credit', key: row.credit_report_key },
        { label: 'AML', kind: 'aml', key: row.aml_report_key },
        { label: 'JSON', kind: 'json', key: row.result_json_key },
    ]

    const download = async (kind: FileKind) => {
        if (busyKind) return
        setBusyKind(kind)
        try {
            const response = await api({ url: BATCH_FILE_URL, data: { rowId: row.id, kind } })
            if (!response.data?.success || !response.data.url) {
                throw new Error(response.data?.message || 'Could not prepare the download.')
            }
            window.open(response.data.url, '_blank', 'noopener,noreferrer')
        } catch (error: any) {
            Report.failure(
                'Download failed',
                error?.response?.data?.message || error?.message || 'Could not download this file.',
                'Okay'
            )
        } finally {
            setBusyKind(null)
        }
    }

    return (
        <span className={tagContainerClass(layout)}>
            {files.map((file) => {
                const missing = !file.key
                const copied = row.status === 'duplicate' && Boolean(file.key)
                const title = missing
                    ? `${file.label} not saved`
                    : copied
                      ? `Copied from original — download ${file.key}`
                      : `Download ${file.key}`
                return (
                    <button
                        key={file.kind}
                        type="button"
                        className={`${styles.fileTag} ${missing || copied ? styles.fileTagMissing : ''}`}
                        title={title}
                        onClick={(event) => {
                            // The table row itself opens the detail modal.
                            event.stopPropagation()
                            if (!missing) void download(file.kind)
                        }}
                        disabled={missing || busyKind !== null}
                    >
                        {busyKind === file.kind ? '...' : file.label}
                    </button>
                )
            })}
        </span>
    )
}

export const RecordTags = ({ row, layout = 'compact' }: TagProps) => {
    const records = row.records
    const tags = [
        { label: 'Client', present: Boolean(records?.client), title: 'clients row' },
        { label: 'AML', present: Boolean(records?.aml), title: 'aml row' },
        { label: 'Agreements', present: Boolean(records?.agreements), title: 'agreements rows' },
        {
            label: 'Prev Matches',
            present: Boolean(records?.matches),
            title: 'previous_industry_matches rows',
        },
    ]

    return (
        <span className={tagContainerClass(layout)}>
            {tags.map((tag) => (
                <span
                    key={tag.label}
                    className={`${styles.fileTag} ${styles.recordTag} ${tag.present ? '' : styles.fileTagMissing}`}
                    title={tag.present ? `${tag.title} saved` : `${tag.title} not saved`}
                >
                    {tag.label}
                </span>
            ))}
        </span>
    )
}
